import * as THREE from "three";

import BaseRoom from "./base_room.js";
import { config, slots_meshes, slots_components, component_names, createComponents } from "./kids_v1.config.js";

export default class Room extends BaseRoom {
  config = config;
  materials = {};
  group = new THREE.Group();
  slots_meshes = slots_meshes;
  slots_components = slots_components;
  component_names = component_names;
  components = null;
  currentLightBitmap = "11";

  constructor() {
    super();
    this.components = createComponents(this);
    this.loadMaterials();
    window.addEventListener("room:loaded", () => {
      this.applySlotMaterials();
    });
    this.load();
  }

  applySlotMaterials() {
    Object.keys(this.slots_components).forEach((slot) => {
      let component_index = this.slots_components[slot];
      let component = this.getComponent(slot, component_index);
      if (component) component.onSelect();
    });

    // Trigger background color change based on current light state
    const lightBitmap = this.getCurrentLightBitmap();
    window.dispatchEvent(
      new CustomEvent("light:change", {
        detail: { lightBitmap }
      })
    );

    window.dispatchEvent(
      new CustomEvent("attributes:change", {
        detail: this.getAttributes(),
      })
    );
  }

  getCurrentLightBitmap(mask = "") {
    let leftLightStatus = !!(
      this.slots_components.window_left >= 0 &&
      (this.slots_components.window_left_blinds === undefined ||
        this.components.window_left_blinds[
          this.slots_components.window_left_blinds
        ].opaque === false)
    )
      ? "1"
      : "0";
    let rightLightStatus = !!(
      this.slots_components.window_right >= 0 &&
      (this.slots_components.window_right_blinds === undefined ||
        this.components.window_right_blinds[
          this.slots_components.window_right_blinds
        ].opaque === false)
    )
      ? "1"
      : "0";
    
    // Allow mask override
    leftLightStatus = mask[0] && mask[0] !== "?" ? mask[0] : leftLightStatus;
    rightLightStatus = mask[1] && mask[1] !== "?" ? mask[1] : rightLightStatus;
    
    return leftLightStatus + rightLightStatus;
  }

  getLightMapMaterial(mask) {
    const lightBitmap = this.getCurrentLightBitmap(mask);

    const lightMapLookup = {
      "11": this.materials.one_one,
      "01": this.materials.zero_one,
      "10": this.materials.one_zero,
      "00": this.materials.zero_zero
    };

    const lightMap = lightMapLookup[lightBitmap] || this.materials.one_one;

    // Only dispatch event if light state actually changed
    if (this.currentLightBitmap !== lightBitmap) {
      this.currentLightBitmap = lightBitmap;
      window.dispatchEvent(
        new CustomEvent("light:change", {
          detail: { lightBitmap }
        })
      );
    }

    return lightMap;
  }

  load() {
    this.gltfLoader.load(this.config.model, (gltf) => {
      gltf.scene.traverse((child) => {
        if (this.config.debug) console.log("loading child", child.name);
        if (this.slots_meshes["floor"].includes(child.name)) {
          window.floor = child;
        }

        // Fix y-position of the poster (distance from the wall),
        // so it doesn't compete with the wall for the lightmap
        if (child.name === "Poster") {
          child.translateY(0.005);
        }

        child.material = this.getLightMapMaterial();
        child.material.side = THREE.DoubleSide;

        child.visible = !this.config.hidden_meshes_on_load.includes(child.name);
      });
      this.group.add(gltf.scene);

      this.addRoofCovering();

      window.dispatchEvent(new CustomEvent("room:loaded"));
    });
  }

  addRoofCovering() {
    let blindsMesh = null;
    let wandReMesh = null;
    
    this.group.traverse((child) => {
      if (this.slots_meshes["window_right_blinds"].includes(child.name)) {
        blindsMesh = child;
      }
      if (child.name === "Wand_re") {
        wandReMesh = child;
      }
    });

    if (!blindsMesh) {
      console.warn("Could not find window_right_blinds mesh for roof covering reference");
      return;
    }

    if (!wandReMesh) {
      console.warn("Could not find Wand_re mesh for size reference");
      return;
    }

    const boundingBox = new THREE.Box3().setFromObject(wandReMesh);
    const size = boundingBox.getSize(new THREE.Vector3());
    
    // Create a covering that's just large enough to hide the roof back
    const roofGeometry = new THREE.PlaneGeometry(size.x * 1.1, size.z * 1.2);
    const roofMaterial = new THREE.MeshBasicMaterial({ 
      transparent: false,
      side: THREE.DoubleSide
    });
    
    this.roofCovering = new THREE.Mesh(roofGeometry, roofMaterial);
    
    this.roofCovering.position.copy(blindsMesh.position);
    this.roofCovering.position.y += 0.03;  // Slightly forward to avoid z-fighting
    this.roofCovering.position.z += 0.15;  // Slightly up
    this.roofCovering.rotation.copy(blindsMesh.rotation);
    this.roofCovering.name = "RoofCovering";
    
    this.group.add(this.roofCovering);
    
    console.log("Added roof covering mesh based on window_right_blinds position:", this.roofCovering.position);
    console.log("Using Wand_re size:", size.x, "x", size.z);
    
    // Set initial color to match scene background
    this.roofCovering.material.color.copy(window.scene.background);
    
    // Update roof covering color when lighting changes
    window.addEventListener("light:change", () => {
      if (this.roofCovering) {
        this.roofCovering.material.color.copy(window.scene.background);
      }
    });
  }
}
